/**
 * AccountMenu — the signed-in-user dropdown, trigger-agnostic.
 *
 * Holds the menu content (account header + Profile / Theme / Sign out) and
 * wraps whatever trigger you pass as `children`. The workspace renders it
 * from the sidebar footer's profile row (`SidebarUser`); `side`/`align` let
 * the caller place the panel (the footer opens it to the right; a topbar
 * avatar would open it below-end).
 *
 * Content map:
 *   ┌──────────────────────────────────────────────┐
 *   │  [44 avatar] Name                            │
 *   │              email · phone                   │
 *   │              role pill                       │
 *   ├──────────────────────────────────────────────┤
 *   │  Profile                  → /settings/profile│
 *   │  Theme                    ▸ light/dark/sys   │
 *   ├──────────────────────────────────────────────┤
 *   │  Sign out                            (bad)   │
 *   └──────────────────────────────────────────────┘
 *
 * Sign out calls `identity.signOut`, drops the `me` cache, and pushes
 * `/sign-in` (the root `beforeLoad` would bounce there anyway once `me` is
 * null — we just don't make the user wait on it).
 *
 * @see prototype/home/dashboard.html `.avatar` block — V1.1 baseline.
 */
import type { ReactNode } from "react";
import { useNavigate, Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { LogOut, User as UserIcon, Sun, Moon, Monitor, Check } from "lucide-react";
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  StatusPill,
} from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { meQueryKey, type MeUser, type MeCompany } from "../../../lib/session.ts";
import { UserAvatar } from "./user-avatar.tsx";
import { useTheme, type ThemePreference } from "../theme/index.ts";

export interface AccountMenuProps {
  user: MeUser;
  /** Null while the workspace is mid-onboarding; menu still renders sign-out. */
  company: MeCompany;
  /** Panel side relative to the trigger. */
  side?: "top" | "right" | "bottom" | "left";
  /** Panel alignment relative to the trigger. */
  align?: "start" | "center" | "end";
  /**
   * Caller-supplied menu items rendered above Profile, followed by a
   * separator. For surfaces that need an extra destination in this menu —
   * the Store admin hides the topbar, so its sidebar chip adds a "Flint app"
   * link here as the way back to the rest of the workspace. Pass
   * `DropdownMenuItem`s; omit it and the menu is unchanged.
   */
  extraItems?: ReactNode;
  /** The clickable trigger element (avatar chip, sidebar profile row, …). */
  children: ReactNode;
}

const THEME_OPTIONS: ReadonlyArray<{ value: ThemePreference; label: string; Icon: typeof Sun }> = [
  { value: "light", label: "Light", Icon: Sun },
  { value: "dark", label: "Dark", Icon: Moon },
  { value: "system", label: "System", Icon: Monitor },
];

export function AccountMenu({
  user,
  company,
  side = "bottom",
  align = "end",
  extraItems,
  children,
}: AccountMenuProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { preference: themePreference, setPreference: setTheme } = useTheme();
  const signOut = trpc.identity.signOut.useMutation({
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: meQueryKey });
      await navigate({ to: "/sign-in" });
    },
  });

  const ActiveThemeIcon =
    themePreference === "light" ? Sun : themePreference === "dark" ? Moon : Monitor;

  return (
    <DropdownMenuRoot>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>

      <DropdownMenuContent side={side} align={align} sideOffset={8} className="w-64 p-0">
        {/* Header tile — name, email, role pill. */}
        <div className="flex items-start gap-3 p-3 border-b border-line">
          <UserAvatar name={user.name} src={user.avatarUrl} size="lg" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-ink truncate leading-tight">{user.name}</div>
            <div className="text-xs text-muted truncate mt-0.5">{user.email ?? user.phone}</div>
            {company ? (
              <div className="mt-1.5">
                <StatusPill tone="sky" size="xs">
                  {company.role}
                </StatusPill>
              </div>
            ) : null}
          </div>
        </div>

        {extraItems ? (
          <>
            <div className="p-1">{extraItems}</div>
            <DropdownMenuSeparator />
          </>
        ) : null}

        <div className="p-1">
          <DropdownMenuItem asChild>
            <Link to="/settings/profile" className="flex items-center gap-2 w-full">
              <UserIcon size={14} strokeWidth={2} aria-hidden className="text-muted" />
              Profile
            </Link>
          </DropdownMenuItem>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <ActiveThemeIcon size={14} strokeWidth={2} aria-hidden className="text-muted" />
              Theme
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-44">
              {THEME_OPTIONS.map(({ value, label, Icon }) => {
                const isActive = themePreference === value;
                return (
                  <DropdownMenuItem
                    key={value}
                    onSelect={(e) => {
                      e.preventDefault();
                      setTheme(value);
                    }}
                  >
                    <Icon size={14} strokeWidth={2} aria-hidden className="text-muted" />
                    <span className="flex-1">{label}</span>
                    {isActive ? (
                      <Check size={14} strokeWidth={2} aria-hidden className="text-sky-600" />
                    ) : null}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

        </div>

        <DropdownMenuSeparator />

        <div className="p-1">
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              if (signOut.isPending) return;
              signOut.mutate({});
            }}
            disabled={signOut.isPending}
            className="text-bad data-[highlighted]:bg-bad-50 data-[highlighted]:text-bad"
          >
            <LogOut size={14} strokeWidth={2} aria-hidden />
            {signOut.isPending ? "Signing out…" : "Sign out"}
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenuRoot>
  );
}
